import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  LayoutDashboard,
  Package,
  Clock,
  CalendarX,
  LogOut,
  Leaf,
  DollarSign,
  Users,
  ChevronDown,
  Plus,
  X,
  Save,
  ClipboardList,
  CreditCard,
  CalendarDays,
} from "lucide-react";
import { WaitlistTable } from "@/components/admin/WaitlistTable";
import { SubscriptionsList } from "@/components/admin/SubscriptionsList";
import { ExperiencesList } from "@/components/admin/ExperiencesList";
import { AdminDashboardHome } from "@/components/admin/AdminDashboardHome";
import type { PreOrder } from "@shared/schema";

interface DashboardStatsData {
  ordersThisMonth: number;
  ordersAllTime: number;
  revenueThisMonth: number;
  revenueAllTime: number;
  activeSubscriptions: number;
  pendingOrders: number;
  waitlistByState: { state: string; count: number }[];
}

interface TimeEntry {
  id: string;
  orderId: string;
  orderName: string;
  hours: number;
  notes: string;
  date: string;
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  approved: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  building: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  delivered: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
};

const orderStatuses = ["pending", "approved", "building", "delivered"] as const;

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function AdminHeader() {
  const { user, logout } = useAuth();

  return (
    <header role="banner" className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <a
            href="/"
            className="flex items-center gap-2 font-serif text-xl font-semibold"
            data-testid="link-home"
            aria-label="Life Itself Studio - Return to homepage"
          >
            <Leaf className="h-6 w-6 text-primary" aria-hidden="true" />
            <span>
              <span className="text-primary">Life Itself</span> Studio
            </span>
          </a>
          <span className="text-muted-foreground hidden sm:inline" aria-hidden="true">|</span>
          <span className="font-medium hidden sm:inline">Admin Dashboard</span>
        </div>

        <nav className="flex items-center gap-4" aria-label="Admin actions">
          {user && (
            <div className="flex items-center gap-3">
              {user.profileImageUrl && (
                <img
                  src={user.profileImageUrl}
                  alt={`Profile picture for ${user.firstName || user.email || 'Admin'}`}
                  className="h-8 w-8 rounded-full"
                  data-testid="img-profile"
                />
              )}
              <span className="text-sm font-medium hidden sm:inline" data-testid="text-username">
                {user.firstName || user.email || "Admin"}
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout()}
            data-testid="button-logout"
            aria-label="Log out of admin dashboard"
          >
            <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
            Logout
          </Button>
        </nav>
      </div>
    </header>
  );
}

function StatCard({
  title,
  value,
  subValue,
  subLabel,
  icon: Icon,
  testId,
}: {
  title: string;
  value: string | number;
  subValue?: string | number;
  subLabel?: string;
  icon: React.ComponentType<{ className?: string }>;
  testId: string;
}) {
  return (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="mt-2 text-3xl font-semibold">{value}</p>
          {subValue !== undefined && subLabel && (
            <p className="mt-1 text-sm text-muted-foreground">
              {subValue} {subLabel}
            </p>
          )}
        </div>
        <div className="rounded-full bg-primary/10 p-3">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
    </Card>
  );
}

function DashboardStats() {
  const { data: stats, isLoading } = useQuery<DashboardStatsData>({
    queryKey: ["/api/admin/stats"],
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="p-6">
            <Skeleton className="h-20 w-full" />
          </Card>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">Failed to load dashboard stats</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title="Total Orders"
        value={stats.ordersAllTime}
        subValue={stats.ordersThisMonth}
        subLabel="this month"
        icon={Package}
        testId="stat-total-orders"
      />
      <StatCard
        title="Pending Orders"
        value={stats.pendingOrders}
        subLabel="require review"
        icon={Clock}
        testId="stat-pending"
      />
      <StatCard
        title="Revenue"
        value={formatCurrency(stats.revenueAllTime)}
        subValue={formatCurrency(stats.revenueThisMonth)}
        subLabel="this month"
        icon={DollarSign}
        testId="stat-revenue"
      />
      <StatCard
        title="Active Subscriptions"
        value={stats.activeSubscriptions}
        icon={Users}
        testId="stat-subscriptions"
      />
    </div>
  );
}

function OrdersTable() {
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [aiReviewResult, setAiReviewResult] = useState<string | null>(null);

  const { data: orders, isLoading } = useQuery<PreOrder[]>({
    queryKey: ["/api/pre-orders"],
  });

  const filteredOrders = orders?.filter((order) =>
    statusFilter === "all" ? true : order.status === statusFilter
  );

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/pre-orders/${id}/status`, { status });
    },
    onSuccess: () => {
      toast({ title: "Status updated", description: "Order status has been updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/pre-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleApprove = (orderId: string) => {
    updateStatusMutation.mutate({ id: orderId, status: "approved" });
  };

  const handleAiReview = (orderId: string) => {
    // Simulate AI review - in production this would call an actual API
    setAiReviewResult(`AI Review for order ${orderId.slice(0, 8)}: Order looks good. All details verified.`);
    toast({ title: "AI Review Complete", description: "The order has been reviewed by AI." });
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  const selectedOrder = selectedOrderId ? orders?.find((o) => o.id === selectedOrderId) : null;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Orders Management</h3>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {orderStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!filteredOrders || filteredOrders.length === 0 ? (
        <p className="text-muted-foreground text-center py-8" data-testid="text-no-orders">
          No orders found
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Style</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delivery Week</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => (
                <TableRow
                  key={order.id}
                  data-testid="order-row"
                  data-order-id={order.id}
                  className={selectedOrderId === order.id ? "bg-muted" : "cursor-pointer hover:bg-muted/50"}
                  onClick={() => setSelectedOrderId(order.id)}
                >
                  <TableCell className="font-mono text-sm" data-testid={`text-order-id-${order.id}`}>
                    {order.id.slice(0, 8)}...
                  </TableCell>
                  <TableCell data-testid={`text-customer-name-${order.id}`}>
                    {order.name}
                  </TableCell>
                  <TableCell data-testid={`text-customer-email-${order.id}`}>
                    {order.email}
                  </TableCell>
                  <TableCell className="capitalize" data-testid={`text-style-${order.id}`}>
                    {order.terrariumStyle}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={statusColors[order.status || "pending"]}
                      data-testid={`badge-status-${order.id}`}
                    >
                      {order.status || "pending"}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`text-delivery-week-${order.id}`}>
                    {order.deliveryWeek}
                  </TableCell>
                  <TableCell data-testid={`text-total-${order.id}`}>
                    {formatCurrency(order.totalAmount || 0)}
                  </TableCell>
                  <TableCell data-testid={`text-created-${order.id}`}>
                    {formatDate(order.createdAt)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={updateStatusMutation.isPending}
                          data-testid={`button-status-dropdown-${order.id}`}
                        >
                          Update Status
                          <ChevronDown className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {orderStatuses.map((status) => (
                          <DropdownMenuItem
                            key={status}
                            onClick={() => updateStatusMutation.mutate({ id: order.id, status })}
                            disabled={order.status === status}
                            data-testid={`menu-item-status-${status}-${order.id}`}
                          >
                            <Badge
                              variant="secondary"
                              className={`${statusColors[status]} mr-2`}
                            >
                              {status}
                            </Badge>
                            Set to {status}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Order Detail Panel with Approve and AI Review */}
      {selectedOrder && (
        <Card className="mt-6 p-4 border-2" data-testid="order-detail-panel">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold">Order Details: {selectedOrder.name}</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedOrderId(null);
                setAiReviewResult(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-2 text-sm mb-4">
            <p><strong>Email:</strong> {selectedOrder.email}</p>
            <p><strong>Style:</strong> {selectedOrder.terrariumStyle}</p>
            <p><strong>Status:</strong> {selectedOrder.status || "pending"}</p>
            <p><strong>Delivery Week:</strong> {selectedOrder.deliveryWeek}</p>
            <p><strong>Total:</strong> {formatCurrency(selectedOrder.totalAmount || 0)}</p>
          </div>
          <div className="flex gap-2">
            {selectedOrder.status === "pending" && (
              <Button
                onClick={() => handleApprove(selectedOrder.id)}
                disabled={updateStatusMutation.isPending}
                data-testid="btn-approve"
              >
                Approve Order
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleAiReview(selectedOrder.id)}
              data-testid="btn-ai-review"
            >
              AI Review
            </Button>
          </div>
          {aiReviewResult && (
            <div className="mt-4 p-3 bg-muted rounded-md" data-testid="ai-review-result">
              {aiReviewResult}
            </div>
          )}
        </Card>
      )}
    </Card>
  );
}

function TimeTracking() {
  const { toast } = useToast();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [hours, setHours] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const { data: orders } = useQuery<PreOrder[]>({
    queryKey: ["/api/pre-orders"],
  });

  useEffect(() => {
    const saved = localStorage.getItem("timeEntries");
    if (saved) {
      setTimeEntries(JSON.parse(saved));
    }
  }, []);

  const saveTimeEntry = () => {
    if (!selectedOrderId || !hours) {
      toast({
        title: "Missing fields",
        description: "Please select an order and enter hours",
        variant: "destructive",
      });
      return;
    }

    const order = orders?.find((o) => o.id === selectedOrderId);
    const newEntry: TimeEntry = {
      id: crypto.randomUUID(),
      orderId: selectedOrderId,
      orderName: order?.name || "Unknown",
      hours: parseFloat(hours),
      notes,
      date: new Date().toISOString(),
    };

    const updatedEntries = [...timeEntries, newEntry];
    setTimeEntries(updatedEntries);
    localStorage.setItem("timeEntries", JSON.stringify(updatedEntries));

    setSelectedOrderId("");
    setHours("");
    setNotes("");

    toast({
      title: "Time logged",
      description: `${hours} hours logged for order ${order?.name || selectedOrderId.slice(0, 8)}`,
    });
  };

  const deleteTimeEntry = (id: string) => {
    const updatedEntries = timeEntries.filter((e) => e.id !== id);
    setTimeEntries(updatedEntries);
    localStorage.setItem("timeEntries", JSON.stringify(updatedEntries));
    toast({ title: "Entry deleted" });
  };

  const totalHours = timeEntries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Log Time</h3>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-1">
            <label className="text-sm font-medium mb-2 block">Order</label>
            <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
              <SelectTrigger data-testid="select-order">
                <SelectValue placeholder="Select order" />
              </SelectTrigger>
              <SelectContent>
                {orders?.map((order) => (
                  <SelectItem key={order.id} value={order.id}>
                    {order.name} - {order.terrariumStyle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <label className="text-sm font-medium mb-2 block">Hours</label>
            <Input
              type="number"
              step="0.5"
              min="0"
              placeholder="Hours spent"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              data-testid="input-hours"
            />
          </div>
          <div className="md:col-span-1">
            <label className="text-sm font-medium mb-2 block">Notes</label>
            <Input
              placeholder="Optional notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="input-notes"
            />
          </div>
          <div className="md:col-span-1 flex items-end">
            <Button onClick={saveTimeEntry} className="w-full" data-testid="button-log-time">
              <Save className="h-4 w-4 mr-2" />
              Log Time
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <h3 className="text-lg font-semibold">Time Entries</h3>
          <Badge variant="secondary" data-testid="badge-total-hours">
            Total: {totalHours.toFixed(1)} hours
          </Badge>
        </div>

        {timeEntries.length === 0 ? (
          <p className="text-muted-foreground text-center py-8" data-testid="text-no-entries">
            No time entries yet
          </p>
        ) : (
          <Table data-testid="table-time-entries">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timeEntries
                .slice()
                .reverse()
                .map((entry) => (
                  <TableRow key={entry.id} data-testid={`row-time-entry-${entry.id}`}>
                    <TableCell>{formatDate(entry.date)}</TableCell>
                    <TableCell>{entry.orderName}</TableCell>
                    <TableCell>{entry.hours}h</TableCell>
                    <TableCell className="max-w-xs truncate">{entry.notes || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteTimeEntry(entry.id)}
                        data-testid={`button-delete-entry-${entry.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function BlockedWeeksManagement() {
  const { toast } = useToast();
  const [newWeek, setNewWeek] = useState<string>("");

  const { data: blockedWeeks, isLoading } = useQuery<string[]>({
    queryKey: ["/api/blocked-weeks"],
  });

  const addBlockedWeekMutation = useMutation({
    mutationFn: async (weekStart: string) => {
      return apiRequest("POST", "/api/blocked-weeks", { weekStart });
    },
    onSuccess: () => {
      toast({ title: "Week blocked", description: "The week has been marked as unavailable." });
      queryClient.invalidateQueries({ queryKey: ["/api/blocked-weeks"] });
      setNewWeek("");
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const removeBlockedWeekMutation = useMutation({
    mutationFn: async (weekStart: string) => {
      return apiRequest("DELETE", `/api/blocked-weeks/${encodeURIComponent(weekStart)}`);
    },
    onSuccess: () => {
      toast({ title: "Week unblocked", description: "The week is now available for delivery." });
      queryClient.invalidateQueries({ queryKey: ["/api/blocked-weeks"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const generateUpcomingWeeks = () => {
    const weeks: { start: string; label: string }[] = [];
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1);

    for (let i = 0; i < 12; i++) {
      const weekStart = new Date(startOfWeek);
      weekStart.setDate(startOfWeek.getDate() + i * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startStr = weekStart.toISOString().split("T")[0];
      const label = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

      weeks.push({ start: startStr, label });
    }

    return weeks;
  };

  const upcomingWeeks = generateUpcomingWeeks();

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Add Blocked Week</h3>
        <div className="flex gap-4 flex-wrap">
          <Select value={newWeek} onValueChange={setNewWeek}>
            <SelectTrigger className="w-64" data-testid="select-week">
              <SelectValue placeholder="Select week to block" />
            </SelectTrigger>
            <SelectContent>
              {upcomingWeeks
                .filter((w) => !blockedWeeks?.includes(w.start))
                .map((week) => (
                  <SelectItem key={week.start} value={week.start}>
                    {week.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => newWeek && addBlockedWeekMutation.mutate(newWeek)}
            disabled={!newWeek || addBlockedWeekMutation.isPending}
            data-testid="button-block-week"
          >
            <Plus className="h-4 w-4 mr-2" />
            Block Week
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Blocked Weeks Calendar</h3>

        {!blockedWeeks || blockedWeeks.length === 0 ? (
          <p className="text-muted-foreground text-center py-8" data-testid="text-no-blocked-weeks">
            No weeks are currently blocked
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {upcomingWeeks.map((week) => {
              const isBlocked = blockedWeeks.includes(week.start);
              return (
                <div
                  key={week.start}
                  className={`p-4 rounded-md border flex items-center justify-between gap-2 ${
                    isBlocked
                      ? "bg-destructive/10 border-destructive/30"
                      : "bg-muted/30 border-border"
                  }`}
                  data-testid={`week-${week.start}`}
                >
                  <div>
                    <p className={`font-medium ${isBlocked ? "text-destructive" : ""}`}>
                      {week.label}
                    </p>
                    {isBlocked && (
                      <Badge variant="destructive" className="mt-1">
                        Blocked
                      </Badge>
                    )}
                  </div>
                  {isBlocked && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBlockedWeekMutation.mutate(week.start)}
                      disabled={removeBlockedWeekMutation.isPending}
                      data-testid={`button-unblock-${week.start}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function AdminContent() {
  const [activeTab, setActiveTab] = useState("overview");

  const handleNavigateToTab = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="tabs-admin">
      <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-grid">
        <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
          <LayoutDashboard className="h-4 w-4" />
          <span className="hidden sm:inline">Overview</span>
        </TabsTrigger>
        <TabsTrigger value="orders" className="gap-2" data-testid="tab-orders">
          <Package className="h-4 w-4" />
          <span className="hidden sm:inline">Orders</span>
        </TabsTrigger>
        <TabsTrigger value="waitlist" className="gap-2" data-testid="tab-waitlist">
          <ClipboardList className="h-4 w-4" />
          <span className="hidden sm:inline">Waitlist</span>
        </TabsTrigger>
        <TabsTrigger value="subscriptions" className="gap-2" data-testid="tab-subscriptions">
          <CreditCard className="h-4 w-4" />
          <span className="hidden sm:inline">Subscriptions</span>
        </TabsTrigger>
        <TabsTrigger value="experiences" className="gap-2" data-testid="tab-experiences">
          <CalendarDays className="h-4 w-4" />
          <span className="hidden sm:inline">Experiences</span>
        </TabsTrigger>
        <TabsTrigger value="time" className="gap-2" data-testid="tab-time">
          <Clock className="h-4 w-4" />
          <span className="hidden sm:inline">Time Tracking</span>
        </TabsTrigger>
        <TabsTrigger value="blocked" className="gap-2" data-testid="tab-blocked">
          <CalendarX className="h-4 w-4" />
          <span className="hidden sm:inline">Blocked Weeks</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview">
        <AdminDashboardHome onNavigateToTab={handleNavigateToTab} />
      </TabsContent>

      <TabsContent value="orders">
        <OrdersTable />
      </TabsContent>

      <TabsContent value="waitlist">
        <WaitlistTable />
      </TabsContent>

      <TabsContent value="subscriptions">
        <SubscriptionsList />
      </TabsContent>

      <TabsContent value="experiences">
        <ExperiencesList />
      </TabsContent>

      <TabsContent value="time">
        <TimeTracking />
      </TabsContent>

      <TabsContent value="blocked">
        <BlockedWeeksManagement />
      </TabsContent>
    </Tabs>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-6 w-32" />
          </div>
          <Skeleton className="h-8 w-24" />
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Skeleton className="h-12 w-96 mb-8" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </main>
    </div>
  );
}

function LoginPrompt() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="p-8 max-w-md w-full mx-4" data-testid="card-login-prompt">
        <div className="text-center">
          <Leaf className="h-12 w-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2" data-testid="text-access-denied">
            Admin Access Required
          </h1>
          <p className="text-muted-foreground mb-6">
            Please log in with your Replit account to access the admin dashboard.
          </p>
          <Button
            size="lg"
            onClick={() => (window.location.href = "/api/login")}
            data-testid="button-login"
          >
            Login with Replit
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    return <LoginPrompt />;
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <AdminHeader />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold" data-testid="text-dashboard-title">
            Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage orders, track time, and control delivery availability
          </p>
        </div>
        <AdminContent />
      </main>
    </div>
  );
}
